import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Input as ShadcnInput } from '@/components/ui/input';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';

/**
 * COMPATIBILITY SHIM — legacy Input API over shadcn's Input + Field.
 *
 * Keeps label/hint/error/adornments so the 6 call sites are untouched. Field
 * handles the invalid wiring via data-invalid; aria-invalid stays on the
 * control itself.
 */

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string | null;
  leftAdornment?: ReactNode;
  rightAdornment?: ReactNode;
  containerStyle?: React.CSSProperties;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leftAdornment, rightAdornment, containerStyle, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-err` : undefined;
  const hasError = !!error;

  const control = (
    <ShadcnInput
      ref={ref}
      id={fieldId}
      aria-invalid={hasError || undefined}
      aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
      className={cn(
        leftAdornment && 'pl-9',
        rightAdornment && 'pr-9',
        className,
      )}
      {...rest}
    />
  );

  return (
    <Field data-invalid={hasError || undefined} style={containerStyle}>
      {label && <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>}
      {leftAdornment || rightAdornment ? (
        <div className="relative flex items-center">
          {leftAdornment && (
            <span className="pointer-events-none absolute left-3 flex text-muted-foreground">
              {leftAdornment}
            </span>
          )}
          {control}
          {rightAdornment && (
            <span className="absolute right-3 flex text-muted-foreground">{rightAdornment}</span>
          )}
        </div>
      ) : (
        control
      )}
      {hint && !error && <FieldDescription id={hintId}>{hint}</FieldDescription>}
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </Field>
  );
});
